import { ApiService } from './api.service';

// Types
export enum VoiceProvider {
  SERVERT = 'servert',
  SERVERE = 'servere',
}

export interface Voice {
  id: string;
  name: string;
  language: string;
  gender: string;
  audio_sample?: string;
  image?: string;
  styles?: string[];
  provider: VoiceProvider;
}

export interface CountryInfo {
  code: string;
  name: string;
  flag: string;
}

export interface VoiceGroup {
  country: CountryInfo;
  voices: Voice[];
}

export interface VoiceProviderInfo {
  id: VoiceProvider;
  name: string;
  description: string;
}

interface VoiceProviderResponse {
  success: boolean;
  data: {
    providers: VoiceProviderInfo[];
  };
}

interface VoicesResponse {
  success: boolean;
  data: VoiceGroup[];
}

interface SpeechResponse {
  success: boolean;
  data: string;
}

interface SpeechStatusResponse {
  success: boolean;
  data: {
    status: string;
    audio_url?: string;
  };
}

interface AudioHistoryResponse {
  status?: 'success' | 'error';
  success?: boolean;
  data?: {
    audioFiles: any[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
  error?: string;
}

/**
 * Get available voice providers
 */
export async function getVoiceProviders(): Promise<VoiceProviderInfo[]> {
  try {
    const response = await ApiService.get<{ success: boolean; data: { providers: VoiceProviderInfo[] } }>('/voices/providers');
    return response.data.providers;
  } catch (error) {
    console.error('Error getting providers:', error);
    throw error;
  }
}

/**
 * Get available voices grouped by language
 */
export async function getVoices(provider = VoiceProvider.SERVERT): Promise<VoiceGroup[]> {
  try {
    const response = await ApiService.get<{ success: boolean; data: VoiceGroup[] }>(`/voices/voices?provider=${provider}`);
    return response.data;
  } catch (error) {
    console.error('Error getting voices:', error);
    throw error;
  }
}

/**
 * Generate speech from text
 */
export async function generateSpeech(params: {
  text: string;
  voiceId: string;
  provider?: VoiceProvider;
  style?: string;
  tempo?: number;
  pitch?: number;
}): Promise<string | { error: string; requiredTokens?: number }> {
  try {
    // Định nghĩa type cho response
    interface GenerateSpeechResponse {
      success: boolean;
      status?: 'success' | 'error';
      data?: string;
      audioUrl?: string;
      error?: string;
    }
    
    // Gọi API để tạo giọng nói
    const response = await ApiService.post<GenerateSpeechResponse>('/voices/generate', params);
    
    // Kiểm tra phản hồi theo định dạng từ server
    if (response.status === 'success' && response.audioUrl) {
      return response.audioUrl;
    } else if (response.success && response.data) {
      return response.data;
    } else if (response.error) {
      throw new Error(response.error);
    } else {
      console.error('Unexpected API response:', response);
      throw new Error('Unknown error generating speech');
    }
  } catch (error: any) {
    console.error('Error generating speech:', error);
    
    // Handle specific error cases
    if (error.message?.includes('Insufficient token balance')) {
      const matches = error.message.match(/Required: (\d+), Available: (\d+)/);
      if (matches && matches.length > 2) {
        const requiredTokens = parseInt(matches[1]);
        return { error: 'insufficient_tokens', requiredTokens };
      }
      return { error: 'insufficient_tokens' };
    }
    
    throw error;
  }
}

/**
 * Check speech generation status
 */
export async function checkSpeechStatus(speakUrl: string): Promise<{ status: string; audio_url?: string }> {
  try {
    const response = await ApiService.get<{ status: 'success' | 'error'; data?: { status: string; audio_url?: string }; error?: string }>(`/voices/presigned-speak-url?speakUrl=${encodeURIComponent(speakUrl)}`);
    
    if (response.status === 'success' && response.data) {
      return response.data;
    } else {
      return { status: 'error' };
    }
  } catch (error) {
    console.error('Error checking status:', error);
    return { status: 'error' };
  }
}

/**
 * Get audio file history for the current user
 */
export async function getAudioHistory(page = 1, limit = 10): Promise<{ audioFiles: any[]; pagination: any }> {
  try {
    const response = await ApiService.get<AudioHistoryResponse>(`/voices/audio-history?page=${page}&limit=${limit}`);
    
    if (response.status === 'success' && response.data) {
      return response.data;
    }
    
    if (response.success && response.data) {
      return response.data;
    }
    
    console.warn('No valid data found in audio history response:', response);
    return { audioFiles: [], pagination: { total: 0, page, limit, pages: 0 } };
  } catch (error) {
    console.error('Error fetching audio history:', error);
    return { audioFiles: [], pagination: { total: 0, page, limit, pages: 0 } };
  }
} 